import type { Patients } from '@/types/patient';
import type { User } from '@/types/users';
import { formatDate } from '@/utils';

// Helper functions to map codes to labels
const getGenderLabel = (
	genderCode: string | number | undefined | null,
): string => {
	const genderMap: Record<string, string> = {
		'1': 'Male',
		'2': 'Female',
	};
	return genderCode ? genderMap[String(genderCode)] || String(genderCode) : '-';
};

const getContactMethodLabel = (
	contactMethodCode: string | number | undefined | null,
): string => {
	const contactMethodMap: Record<string, string> = {
		'1': 'Any',
		'2': 'Email',
		'3': 'Phone',
		'4': 'Fax',
		'5': 'Mail',
	};
	return contactMethodCode
		? contactMethodMap[String(contactMethodCode)] || String(contactMethodCode)
		: '-';
};

// Define the data structure types for DetailsModal
export interface DetailField {
	label: string;
	value: string | number | undefined | null | string[];
}

export interface DetailColumn {
	data: DetailField[];
}

export const parsePatientDataForDetailsModal = (
	patientData: Patients,
): DetailColumn[] => {
	return [
		{
			data: [
				{
					label: "Patient's First Name",
					value: patientData.firstname || patientData.fullname?.split(' ')[0],
				},
				{
					label: "Patient's Last Name",
					value:
						patientData.lastname ||
						patientData.fullname?.split(' ').slice(1).join(' '),
				},
				{ label: 'DOB', value: patientData.birthdate },
				{ label: 'EMR ID', value: patientData.ta_emrid },
				{ label: 'Gender', value: getGenderLabel(patientData.gendercode) },
				{ label: 'Mobile Phone', value: patientData.mobilephone },
				{ label: 'Email', value: patientData.emailaddress1 },
			],
		},
		{
			data: [
				{
					label: 'Contact Method',
					value: getContactMethodLabel(patientData.preferredcontactmethodcode),
				},
				{ label: 'Clinic', value: patientData.account?.name },
				{
					label: 'Primary Insurance',
					value: patientData.primaryInsurance?.ta_name,
				},
				{ label: 'Primary Member ID', value: patientData.ta_primarymemberid },
				{
					label: 'Secondary Insurance',
					value: patientData.secondaryInsurance?.ta_name,
				},
				{
					label: 'Secondary Member ID',
					value: patientData.ta_secondarymemberid,
				},
			],
		},
		{
			data: [
				{ label: 'Street 1', value: patientData.address1_line1 },
				{
					label: 'State/Province',
					value: patientData.address1_stateorprovince,
				},
				{ label: 'City', value: patientData.address1_city },
				{ label: 'Postal Code', value: patientData.address1_postalcode },
			],
		},
	];
};

export const parseUserDataForDetailsModal = (
	userData: User,
): DetailColumn[] => {
	return [
		{
			data: [
				{
					label: 'First Name',
					value: userData.firstName || userData.lastName?.split(' ')[0],
				},
				{
					label: 'Last Name',
					value:
						userData.lastName ||
						userData.lastName?.split(' ').slice(1).join(' '),
				},
				{ label: 'Email', value: userData.email },
				{
					label: 'User Type',
					value: userData.isExternal ? 'External' : 'Internal',
				},
			],
		},
		{
			data: [
				{
					label: 'Creation Date',
					value: formatDate(userData.createdAt?.toString() || ''),
				},
				{
					label: 'Last Active',
					value: userData.lastAccess
						? formatDate(userData.lastAccess.toString())
						: '-',
				},
				{ label: 'Role', value: userData.role },
			],
		},
		{
			data: [
				{
					label: 'Auto Sync Clinics',
					value: userData.autoSyncClinics ? 'Yes' : 'No',
				},
				{ label: 'Port', value: userData.portsAvailable },

				{ label: 'Status', value: userData.status ? 'Active' : 'Inactive' },
			],
		},
		{
			data: [
				{
					label: 'Environments Available',
					value: userData.environmentsAvailable,
				},
				{
					label: 'Clinics',
					value: userData.clinicsDetails?.map((clinic) => clinic.name) || [],
				},
				{ label: 'Permissions', value: userData.permissions },
			],
		},
		{
			data: [
				{
					label: 'Locations',
					value:
						userData.locationsDetails?.map((location) => location.name) || [],
				},
				{
					label: 'Sales Reps',
					value:
						userData.salesRepsDetails?.map((salesRep) => salesRep.name) || [],
				},
			],
		},
	];
};

export const parseGenericDataForDetailsModal = (
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	data: Record<string, any>,
	fieldMappings?: Record<string, string>,
): DetailColumn[] => {
	const fields: DetailField[] = Object.entries(data).map(([key, value]) => ({
		label: fieldMappings?.[key] || key,
		value: value,
	}));

	// Split fields into columns (3 fields per column for better layout)
	const columns: DetailColumn[] = [];
	const fieldsPerColumn = 3;

	for (let i = 0; i < fields.length; i += fieldsPerColumn) {
		columns.push({
			data: fields.slice(i, i + fieldsPerColumn),
		});
	}

	return columns;
};
